import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/contexts/AuthContext";
import CharacterCard from "@/components/CharacterCard";
import AgeVerifyModal from "@/components/AgeVerifyModal";
import UserAvatar from "@/components/UserAvatar";
import { fileToResizedDataUrl } from "@/lib/image";
import {
  LogOut, Sparkles, ShieldCheck, Camera, Pencil, Check, X, ExternalLink, Plus, Trash2, Star,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BIO_MAX = 500;

function CharsGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-white/[0.03]" />
      ))}
    </div>
  );
}

function FavoritesTab() {
  const [favs, setFavs] = useState(null);
  useEffect(() => {
    api.get("/me/favorites").then(({ data }) => setFavs(data.characters)).catch(() => setFavs([]));
  }, []);
  if (favs === null) return <CharsGridSkeleton />;
  if (favs.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center">
        <div className="font-display text-xl text-white">No favorites yet.</div>
        <p className="mt-2 text-xs text-neutral-500">Tap the heart on any character to save them here.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="profile-favs-grid">
      {favs.map((c, i) => (
        <CharacterCard
          key={c.id}
          character={c}
          testIdPrefix="profile-fav"
          onEngagementChange={(patch) => setFavs((prev) => {
            // If unfavorited from this view, drop it from the list.
            if (patch.is_favorited === false) return prev.filter((x) => x.id !== c.id);
            return prev.map((x, j) => (j === i ? { ...x, ...patch } : x));
          })}
        />
      ))}
    </div>
  );
}

function PersonasTab() {
  const nav = useNavigate();
  const [personas, setPersonas] = useState(null);

  const load = () => {
    api.get("/personas").then(({ data }) => setPersonas(data.personas)).catch(() => setPersonas([]));
  };
  useEffect(load, []);

  async function del(id) {
    if (!confirm("Delete this persona?")) return;
    await api.delete(`/personas/${id}`);
    load();
  }

  if (personas === null) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-2xl bg-white/[0.03]" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button
          data-testid="profile-personas-new-btn"
          onClick={() => nav("/create-persona")}
          className="btn-pill btn-primary text-xs py-2 px-3 inline-flex items-center gap-1"
        >
          <Plus size={14} /> New persona
        </button>
      </div>
      {personas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center" data-testid="profile-personas-empty">
          <div className="font-display text-xl text-white">No personas yet</div>
          <p className="mt-2 text-xs text-neutral-500">Create one to roleplay as a distinct character in your chats.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="profile-personas-list">
          {personas.map((p) => (
            <li key={p.id} className="flex items-start gap-3 rounded-2xl border border-white/5 bg-white/[0.02] p-3">
              {p.avatar_url ? (
                <img src={p.avatar_url} alt="" className="h-14 w-14 rounded-xl object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-fuchsia-500/20 font-display text-xl text-fuchsia-200">
                  {p.name[0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg text-white truncate">{p.name}</span>
                  {p.is_default && <Star size={12} className="text-yellow-400 fill-yellow-400" />}
                </div>
                <p className="text-xs text-neutral-400 line-clamp-2">{p.description}</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => nav(`/edit-persona/${p.id}`)}
                  data-testid={`profile-persona-edit-${p.id}`}
                  className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
                >
                  Edit
                </button>
                <button
                  onClick={() => del(p.id)}
                  data-testid={`profile-persona-delete-${p.id}`}
                  className="rounded-lg bg-white/5 p-1.5 text-neutral-400 hover:bg-white/10 hover:text-rose-300"
                  aria-label="Delete persona"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Profile() {
  const nav = useNavigate();
  const { user, logout, updateProfile, refresh, setSessionUser } = useAuth();
  const [mine, setMine] = useState(null);  // null = loading
  const [verifyOpen, setVerifyOpen] = useState(false);
  const followers = user?.followers ?? 0;
  const following = user?.following ?? 0;
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState(user?.bio || "");
  const [savingBio, setSavingBio] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // /characters/mine is enriched (like_count etc) so this may include
    // some ObjectId-ish payload — keep it non-blocking with a skeleton.
    api.get("/characters/mine")
      .then(({ data }) => setMine(data.characters))
      .catch(() => setMine([]));
  }, []);

  useEffect(() => { setBioDraft(user?.bio || ""); }, [user?.bio]);

  async function doLogout() {
    await logout();
    nav("/welcome", { replace: true });
  }

  async function saveBio() {
    if (bioDraft.length > BIO_MAX) return;
    setSavingBio(true);
    try {
      await updateProfile({ bio: bioDraft.trim() });
      setEditingBio(false);
      toast.success("Bio saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save bio.");
    } finally {
      setSavingBio(false);
    }
  }

  async function onAvatarPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadingAvatar(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, { maxSize: 256, quality: 0.85 });
      const { data } = await api.post("/auth/me/avatar", { data_url: dataUrl });
      setSessionUser(data.user);
      toast.success("Avatar updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload avatar.");
    } finally {
      setUploadingAvatar(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className="relative shrink-0">
            <UserAvatar src={user?.avatar_url} name={user?.username} size={72} testId="profile-avatar" />
            <button
              data-testid="profile-avatar-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              aria-label="Change avatar"
              className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-violet-500 text-white shadow-lg shadow-violet-500/40 hover:bg-violet-400 disabled:opacity-50"
            >
              <Camera size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onAvatarPick}
              className="hidden"
              data-testid="profile-avatar-file-input"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Profile</div>
              {user?.is_admin && (
                <span data-testid="profile-admin-badge" className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500/20 to-rose-500/20 border border-amber-400/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-200">
                  <ShieldCheck size={10} /> Creator
                </span>
              )}
              {user?.is_plus && !user?.is_admin && (
                <span data-testid="profile-plus-badge" className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-200">
                  <Sparkles size={10} /> Immersive+
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl leading-tight text-white truncate">@{user?.username}</h1>
            <div className="mt-2 flex items-center gap-4 text-sm">
              <span data-testid="profile-followers-count"><span className="font-medium text-white">{followers}</span> <span className="text-neutral-400">followers</span></span>
              <span data-testid="profile-following-count"><span className="font-medium text-white">{following}</span> <span className="text-neutral-400">following</span></span>
            </div>
            {user?.username && (
              <Link
                to={`/u/${user.username}`}
                data-testid="profile-view-public-link"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-violet-300 hover:text-violet-200"
              >
                View public profile <ExternalLink size={10} />
              </Link>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button data-testid="profile-logout-btn" onClick={doLogout} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10" aria-label="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Bio */}
      <section className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        {editingBio ? (
          <div>
            <textarea
              data-testid="profile-bio-textarea"
              value={bioDraft}
              maxLength={BIO_MAX}
              onChange={(e) => setBioDraft(e.target.value)}
              rows={4}
              placeholder="Tell people about the stories you love, the characters you write, or just a vibe…"
              className="w-full resize-none rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-white placeholder:text-neutral-500 focus:border-violet-500/50 focus:outline-none"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-neutral-500">{bioDraft.length}/{BIO_MAX}</span>
              <div className="flex items-center gap-2">
                <button
                  data-testid="profile-bio-cancel-btn"
                  onClick={() => { setEditingBio(false); setBioDraft(user?.bio || ""); }}
                  className="btn-pill btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1"
                >
                  <X size={12} /> Cancel
                </button>
                <button
                  data-testid="profile-bio-save-btn"
                  onClick={saveBio}
                  disabled={savingBio}
                  className="btn-pill btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <Check size={12} /> Save
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Bio</div>
              {user?.bio ? (
                <p data-testid="profile-bio-text" className="text-sm text-neutral-200 whitespace-pre-wrap break-words">
                  {user.bio}
                </p>
              ) : (
                <p data-testid="profile-bio-empty" className="text-sm text-neutral-500">
                  Add a bio so people know what stories you love.
                </p>
              )}
            </div>
            <button
              data-testid="profile-bio-edit-btn"
              onClick={() => setEditingBio(true)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
              aria-label="Edit bio"
            >
              <Pencil size={14} />
            </button>
          </div>
        )}
      </section>

      {!user?.is_plus && !user?.preview_mode && (
        <Link to="/upgrade" data-testid="profile-upgrade-cta" className="mb-6 block rounded-2xl border border-violet-500/30 bg-gradient-to-r from-violet-500/15 to-fuchsia-500/10 p-4 hover:from-violet-500/20">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/30 text-violet-100"><Sparkles size={18} /></span>
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Upgrade to Immersive+</div>
              <div className="text-[11px] text-neutral-400">Unlimited chats, characters & personas for $14.99/mo</div>
            </div>
            <span className="text-violet-200">→</span>
          </div>
        </Link>
      )}

      <div className="mb-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white flex items-center gap-2">
              Show NSFW content
              {user?.nsfw_verified && <ShieldCheck size={14} className="text-emerald-400" />}
            </div>
            <div className="text-xs text-neutral-500">
              {user?.nsfw_verified ? "Age verified. Enables 18+ characters on Discover." : "Requires age verification to enable."}
            </div>
          </div>
          <Switch
            data-testid="profile-nsfw-toggle"
            checked={!!user?.nsfw_enabled}
            onCheckedChange={async (v) => {
              if (v && !user?.nsfw_verified) { setVerifyOpen(true); return; }
              try { await updateProfile({ nsfw_enabled: !!v }); }
              catch (e) { toast.error(e.response?.data?.detail || "Could not toggle."); }
            }}
          />
        </div>
      </div>

      <Tabs defaultValue="chars" className="w-full">
        <TabsList className="bg-white/5 border border-white/5">
          <TabsTrigger value="chars" data-testid="profile-tab-chars">My characters</TabsTrigger>
          <TabsTrigger value="favs" data-testid="profile-tab-favs">My favorites</TabsTrigger>
          <TabsTrigger value="personas" data-testid="profile-tab-personas">My personas</TabsTrigger>
        </TabsList>
        <TabsContent value="chars" className="mt-4">
          {mine === null ? (
            <CharsGridSkeleton />
          ) : mine.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center">
              <div className="font-display text-xl text-white">No characters yet.</div>
              <Link data-testid="profile-empty-create-btn" to="/create" className="btn-pill btn-primary mt-4 inline-flex">
                Create your first
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="profile-chars-grid">
              {mine.map((c) => <CharacterCard key={c.id} character={c} testIdPrefix="profile" />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="favs" className="mt-4">
          <FavoritesTab />
        </TabsContent>
        <TabsContent value="personas" className="mt-4">
          <PersonasTab />
        </TabsContent>
      </Tabs>

      <BottomNav />
      <AgeVerifyModal
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        onVerified={async () => { await refresh(); await updateProfile({ nsfw_enabled: true }); }}
      />
    </div>
  );
}
